import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { ActivatedRoute, Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor
{
    /**
     * Constructor
     */
    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private _authService: AuthService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _httpBackend: HttpBackend
    )
    {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
    {
        // Clone the request object
        let newReq = req.clone();

        // QR sessions: bypass MsalInterceptor entirely by dispatching via HttpBackend.
        // MsalInterceptor would try acquireTokenSilent() which fails without an MSAL account.
        if ( this._authService.isQrSession )
        {
            // Unauthenticated QR session endpoints — pass through without token
            if ( req.url.includes('/api/qr-session/create') || (req.url.includes('/status') && req.url.includes('/api/qr-session/')) )
            {
                return this._httpBackend.handle(newReq);
            }

            if ( this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken) )
            {
                newReq = req.clone({
                    headers: req.headers.set('Authorization', 'Bearer ' + this._authService.accessToken),
                    url: (req.url.startsWith('/api/') ? environment.apiConfig.baseUrl : '') + req.url
                });

                return this._httpBackend.handle(newReq).pipe(
                    catchError((error) => {
                        if ( error instanceof HttpErrorResponse && error.status === 401 )
                        {
                            this._authService.signOut();
                            localStorage.setItem('qrSessionExpired', 'true');
                            this._router.navigateByUrl('/sign-in');
                        }
                        return throwError(error);
                    })
                );
            }
            else
            {
                // QR session token expired
                this._authService.signOut();
                localStorage.setItem('qrSessionExpired', 'true');
                this._router.navigateByUrl('/sign-in');
                return throwError(() => new Error('QR session expired'));
            }
        }

        // Skip auth handling for unauthenticated QR session endpoints (non-QR session, e.g. desktop before sign-in)
        if ( req.url.includes('/api/qr-session/create') || (req.url.includes('/status') && req.url.includes('/api/qr-session/')) )
        {
            return next.handle(newReq);
        }

        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if ( this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken) )
        {
            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + this._authService.accessToken),
                url: (req.url.startsWith('/api/') ? environment.apiConfig.baseUrl : '') + req.url 
            });
        } else {
            this._authService.signInMsal(this.msalGuardConfig.authRequest)
            .subscribe(user => {
                // Set the redirect url.
                // The '/signed-in-redirect' is a dummy url to catch the request and redirect the user
                // to the correct page after a successful sign in. This way, that url can be set via
                // routing file and we don't have to touch here.

                // navigate to url this._activatedRoute.snapshot
                const urlSegments = this._activatedRoute.snapshot.url;
                this._router.navigate(urlSegments.map(segment => segment.path), { queryParams: this._activatedRoute.snapshot.queryParams });
            });            
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {

                // Catch "401 Unauthorized" responses
                if ( error instanceof HttpErrorResponse && error.status === 401 )
                {
                    // Sign out
                    this._authService.signOut();

                    // Reload the app
                    location.reload();
                }

                return throwError(error);
            })
        );
    }
}
